import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, CheckCircle2, Star, Users, MessageSquare, Briefcase, Phone, Heart, Zap, ShieldCheck, Quote, GraduationCap, Brain, Sparkles, FileText, Eye, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { services, testimonials, siteConfig } from '../data';
import PdfModal from '../components/PdfModal';
import { imageUrl } from '../lib/images';

const Home = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showKortPdfModal, setShowKortPdfModal] = useState(false);
  const itemsPerPage = 3;
  const totalPages = Math.ceil(testimonials.length / itemsPerPage);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % totalPages);
  }, [totalPages]);

  useEffect(() => {
    if (totalPages > 1) {
      const timer = setInterval(() => {
        nextSlide();
      }, 6000);
      return () => clearInterval(timer);
    }
  }, [nextSlide, totalPages]);

  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative bg-transparent overflow-hidden min-h-[600px] lg:min-h-[800px] flex items-center">
        <div className="absolute inset-0 -z-10">
          <motion.img 
            initial={{ scale: 1 }}
            animate={{ scale: 1.1 }}
            transition={{ 
              duration: 20, 
              repeat: Infinity, 
              repeatType: "reverse", 
              ease: "linear" 
            }}
            src="/671649277_1477428311061498_4661077913734140206_n.jpg" 
            alt="Background" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-emerald-950/75" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full relative z-10">
          <div className="flex flex-col items-center text-center">
            <div className="py-20 lg:py-40">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="max-w-3xl"
              >
                <h1 className="text-4xl md:text-6xl lg:text-8xl font-medium text-white leading-[1.1] mb-8 drop-shadow-sm">
                  Dit Liv - <span className="text-emerald-400">Dit Ansvar</span>
                </h1>
                <p className="text-lg lg:text-xl text-white mb-12 leading-relaxed max-w-2xl mx-auto">
                  Genvind energien og overblikket med en videnskabelig tilgang til trivsel. Vi arbejder med Biologi, Mindset og Strategi for at skabe varig forandring.
                </p>
                <div className="flex flex-col sm:flex-row gap-6 justify-center">
                  <Link
                    to="/kontakt"
                    className="bg-emerald-600 text-white px-10 py-5 font-bold text-xl hover:bg-emerald-500 transition-all flex items-center justify-center group shadow-xl"
                  >
                    Book en gratis samtale
                    <ArrowRight className="ml-2 h-6 w-6 group-hover:translate-x-1 transition-transform" />
                  </Link>
                  <Link
                    to="/trivlogi-1-1"
                    className="bg-transparent text-white border-2 border-white hover:border-white px-10 py-5 font-bold text-xl transition-all flex items-center justify-center"
                  >
                    Se 1-1 forløb
                  </Link>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Features/Why Section */}
      <section className="bg-gray-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative h-[500px] lg:h-[600px]">
              <img
                src="/materiale-260501/image00009.jpg"
                alt="Michael Lunde ved flipover"
                className="absolute inset-0 w-full h-full object-cover shadow-2xl"
              />
              <div className="absolute -bottom-6 -right-6 bg-emerald-900 text-white p-8 shadow-xl hidden md:block">
                <p className="text-4xl font-bold mb-1">100%</p>
                <p className="text-emerald-100 font-medium uppercase tracking-wider text-sm">Fortrolighed & Fokus</p>
              </div>
            </div>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-8 leading-tight">
                Hvorfor vælge et forløb eller samarbejde med <br />
                <span className="text-emerald-600">Michael Lunde?</span>
              </h2>
              <div className="space-y-6">
                {[
                  'Fokus på konkrete værktøjer du kan bruge med det samme.',
                  'Videnskabelig og biologisk forståelse af trivsel.',
                  '24/7 fri sparring over tlf/sms under dit forløb.',
                  'Bæredygtige forandringer der holder på den lange bane.',
                  'Mangeårig erfaring med både private og organisationer.'
                ].map((item, i) => (
                  <div key={i} className="flex items-start space-x-4">
                    <div className="mt-1">
                      <CheckCircle2 className="h-6 w-6 text-emerald-900" />
                    </div>
                    <p className="text-lg text-slate-700">{item}</p>
                  </div>
                ))}
              </div>
              <div className="mt-12">
                <Link
                  to="/om"
                  className="inline-block bg-emerald-900 text-white px-10 py-5 font-bold text-lg hover:bg-emerald-950 transition-all shadow-md"
                >
                  Læs mere om min baggrund
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Henvisning til afklarende samtale & kort tekst/PDF */}
      <section className="py-20 bg-emerald-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 -z-10 opacity-15">
          <img 
            src="/materiale-260501/image00001.jpg" 
            alt="Afklarende samtale baggrund" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-900/90 border border-emerald-700 text-emerald-300 text-xs font-semibold">
                <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
                Kort introduktion &amp; gratis afklaring
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-[1.15]">
                Når det hele <br />
                <span className="text-emerald-400">fylder for meget</span>
              </h2>
              <p className="text-xl font-medium text-emerald-100">
                Du behøver ikke have styr på det hele, før du tager det første skridt.
              </p>
              <div className="space-y-3.5 text-base text-emerald-200/90 leading-relaxed max-w-2xl">
                <p>
                  Måske er du presset, stresset eller bare nået dertil, hvor du kan mærke, at noget skal ændre sig.
                </p>
                <p>
                  Når man ikke har det godt, kan selv det at finde ud af, hvilken hjælp man har brug for, føles som endnu en opgave. Derfor behøver du ikke starte med at beslutte dig for et forløb.
                </p>
                <p className="font-semibold text-white pt-1">
                  Du kan starte med en gratis og uforpligtende samtale.
                </p>
              </div>

              {/* 3 Steps summary pills */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
                <div className="p-3.5 bg-emerald-900/60 rounded-xl border border-emerald-800 text-xs">
                  <span className="font-bold text-emerald-300 block mb-0.5">BIOLOGI</span>
                  <span className="text-emerald-100">Forstå hvad der sker i dig.</span>
                </div>
                <div className="p-3.5 bg-emerald-900/60 rounded-xl border border-emerald-800 text-xs">
                  <span className="font-bold text-emerald-300 block mb-0.5">MINDSET</span>
                  <span className="text-emerald-100">Forstå dine mønstre &amp; reaktioner.</span>
                </div>
                <div className="p-3.5 bg-emerald-900/60 rounded-xl border border-emerald-800 text-xs">
                  <span className="font-bold text-emerald-300 block mb-0.5">STRATEGI</span>
                  <span className="text-emerald-100">Konkrete skridt til dit liv.</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-4 pt-4">
                <Link
                  to="/kontakt"
                  className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 px-8 py-4 font-bold text-base rounded-xl transition-all shadow-lg flex items-center gap-2 group"
                >
                  Book en gratis samtale
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <button
                  type="button"
                  onClick={() => setShowKortPdfModal(true)}
                  className="bg-emerald-900 hover:bg-emerald-850 text-white border border-emerald-700 px-6 py-4 font-semibold text-base rounded-xl transition-colors flex items-center gap-2"
                >
                  <Eye className="w-4 h-4 text-emerald-300" />
                  Vis kort PDF
                </button>
                <a
                  href="/_assets/1-1-kort.pdf"
                  download
                  className="bg-emerald-950 hover:bg-emerald-900 text-emerald-300 hover:text-white border border-emerald-800 px-4 py-4 font-medium text-sm rounded-xl transition-colors flex items-center gap-1.5"
                  title="Download 1-1 kort.pdf"
                >
                  <Download className="w-4 h-4" />
                  Download
                </a>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="bg-white text-zinc-900 rounded-3xl p-8 sm:p-10 shadow-2xl border border-emerald-200/40 relative">
                <div className="flex items-center gap-3 mb-6 pb-6 border-b border-zinc-100">
                  <div className="p-3 bg-emerald-100 text-emerald-900 rounded-2xl flex-shrink-0">
                    <FileText className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg text-emerald-950">1-1 Kort overblik</h3>
                    <p className="text-xs text-slate-500">TRIVlogi® · 1 side PDF</p>
                  </div>
                </div>

                <p className="text-sm text-slate-700 leading-relaxed mb-6">
                  Her kan du fortælle lidt om det, der fylder. Du behøver ikke have de rigtige ord eller kunne forklare det hele. Vi tager udgangspunkt i dér, hvor du er, og finder sammen ud af, om jeg og TRIVlogi® er det rigtige næste skridt for dig.
                </p>

                <div className="p-4 bg-emerald-50 rounded-2xl border border-emerald-100 text-xs text-emerald-950 mb-6 space-y-1">
                  <p className="font-bold">11 års erfaring med mennesker og trivsel</p>
                  <p className="text-slate-600">Forståelse skaber mere ro og flere handlemuligheder. Du skal ikke passe ind i en metode – vi finder ud af, hvad der giver mening for dig.</p>
                </div>

                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={() => setShowKortPdfModal(true)}
                    className="w-full inline-flex items-center justify-center gap-2 bg-emerald-900 hover:bg-emerald-800 text-white font-bold py-3.5 px-4 rounded-xl text-sm transition-colors shadow-sm"
                  >
                    <Eye className="w-4 h-4" /> Læs den korte PDF i browseren
                  </button>
                  <div className="flex items-center justify-between text-xs text-slate-500 pt-2 px-1">
                    <Link to="/trivlogi-1-1" className="text-emerald-800 font-bold hover:underline">
                      Se det fulde 1-1 forløb &rarr;
                    </Link>
                    <a href="/_assets/1-1-kort.pdf" download className="text-slate-600 hover:text-emerald-900 flex items-center gap-1 font-medium">
                      <Download className="w-3.5 h-3.5" /> Download fil
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-6">Hvordan kan jeg <span className="text-emerald-600">hjælpe dig</span>?</h2>
            <p className="text-lg text-slate-600">
              Uanset om du er privatperson eller en del af et fællesskab, tilbyder jeg forløb og oplæg, der skaber reel værdi og dybde.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* 1-1 Trivlogi Forløb */}
            <motion.div
              whileHover={{ y: -10 }}
              className="bg-zinc-50 border border-zinc-100 p-0 flex flex-col h-full shadow-sm hover:shadow-xl transition-all overflow-hidden"
            >
              <div className="h-48 overflow-hidden bg-white flex items-center justify-center">
                <img 
                  src="/materiale-260501/image00001.jpg" 
                  alt="Trivlogi 1-1 Forløb" 
                  className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-500"
                />
              </div>
              <div className="p-8 flex flex-col flex-grow">
                <div className="text-xs uppercase tracking-wider font-bold text-emerald-800 mb-2">Private &amp; Individuelt</div>
                <h3 className="text-2xl font-bold text-zinc-900 mb-4">TRIVlogi® 1:1 Forløb</h3>
                <p className="text-slate-600 mb-8 leading-relaxed">
                  Forstå hvad der sker i dig. Find en vej, der giver mening for dig. Når vi forstår kroppens, hjernens og nervesystemets reaktioner, bliver det lettere at finde brugbare handlemuligheder.
                </p>
                <div className="mt-auto">
                  <Link
                    to="/trivlogi-1-1"
                    className="inline-flex items-center text-emerald-800 font-bold hover:text-emerald-950 transition-colors"
                  >
                    Læs mere &amp; se forløbet <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>
              </div>
            </motion.div>

            {/* Trivsel i fællesskabet */}
            <motion.div
              whileHover={{ y: -10 }}
              className="bg-zinc-50 border border-zinc-100 p-0 flex flex-col h-full shadow-sm hover:shadow-xl transition-all overflow-hidden"
            >
              <div className="h-48 overflow-hidden bg-white flex items-center justify-center">
                <img 
                  src={imageUrl('/_assets/mindre-stress-staerkere-faellesskaber.png', 'width=800,quality=85,format=auto')} 
                  alt="Trivsel i fællesskabet" 
                  className="w-full h-full object-contain grayscale hover:grayscale-0 transition-all duration-500"
                />
              </div>
              <div className="p-8 flex flex-col flex-grow">
                <div className="text-xs uppercase tracking-wider font-bold text-emerald-800 mb-2">Virksomheder &amp; Teams</div>
                <h3 className="text-2xl font-bold text-zinc-900 mb-4">Trivsel i fællesskabet</h3>
                <p className="text-slate-600 mb-8 leading-relaxed">
                  Forebyggelse af stress, hjælp når stressen rammer og stærkere arbejdsfællesskaber. Inkluderer foredrag, workshops, konflikthåndtering og ledelsessparring.
                </p>
                <div className="mt-auto">
                  <Link
                    to="/trivsel-i-faellesskabet"
                    className="inline-flex items-center text-emerald-800 font-bold hover:text-emerald-950 transition-colors"
                  >
                    Læs mere &amp; se erhvervspriser <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>
              </div>
            </motion.div>

            {/* Skærmen, hjernen og trivslen */}
            <motion.div
              whileHover={{ y: -10 }}
              className="bg-zinc-50 border border-zinc-100 p-0 flex flex-col h-full shadow-sm hover:shadow-xl transition-all overflow-hidden"
            >
              <div className="h-48 overflow-hidden bg-white flex items-center justify-center">
                <img 
                  src="/materiale-260501/image00007.png" 
                  alt="Skærmen, hjernen og trivslen" 
                  className="w-full h-full object-contain p-4 grayscale hover:grayscale-0 transition-all duration-500"
                />
              </div>
              <div className="p-8 flex flex-col flex-grow">
                <div className="text-xs uppercase tracking-wider font-bold text-emerald-800 mb-2">Folkeskoler &amp; Udskoling</div>
                <h3 className="text-2xl font-bold text-zinc-900 mb-4">Skærmen, hjernen og trivslen</h3>
                <p className="text-slate-600 mb-8 leading-relaxed">
                  Hvordan påvirker den digitale verden hjerner, opmærksomhed og trivsel? Et engagerende oplæg til 7.–10. klasse med afsæt i neurovidenskab og PISA 2025.
                </p>
                <div className="mt-auto">
                  <Link
                    to="/skaermen-hjernen-og-trivslen"
                    className="inline-flex items-center text-emerald-800 font-bold hover:text-emerald-950 transition-colors"
                  >
                    Se oplægget til skoler <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Featured School Talk Banner: PISA 2025 */}
          <div className="mt-16 bg-pine-950 text-white rounded-3xl p-8 md:p-12 shadow-xl border border-pine-800 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-80 h-80 bg-sage-400/10 rounded-full blur-3xl pointer-events-none" />
            <div className="grid lg:grid-cols-12 gap-8 items-center relative z-10">
              <div className="lg:col-span-8 space-y-4">
                <div className="inline-flex items-center gap-2 bg-sage-200 text-pine-950 font-display font-bold text-xs uppercase px-4 py-1.5 rounded-full tracking-wide">
                  <Sparkles className="w-3.5 h-3.5 text-pine-900" />
                  Nyt foredrag til folkeskoler med afsæt i PISA 2025
                </div>
                <h3 className="font-display font-black text-2xl sm:text-3xl lg:text-4xl uppercase tracking-tight text-white leading-tight">
                  Skærmen, hjernen og trivslen
                </h3>
                <p className="text-sm sm:text-base text-sage-100/90 leading-relaxed max-w-2xl font-normal">
                  Hvordan påvirker den digitale verden vores hjerner, vores opmærksomhed og vores trivsel? Et engagerende oplæg til udskolingen – bygget på biologi og neurovidenskab, helt uden løftede pegefingre.
                </p>
                <div className="flex flex-wrap gap-4 text-xs text-sage-200/90 pt-2 font-medium">
                  <span className="flex items-center gap-1.5"><Brain className="w-4 h-4 text-sage-200" /> Dopamin &amp; vaner</span>
                  <span className="flex items-center gap-1.5"><GraduationCap className="w-4 h-4 text-sage-200" /> Målrettet 7. – 10. klasse</span>
                  <span className="flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-sage-200" /> Frivilligt 14-dages eksperiment</span>
                </div>
              </div>

              <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3 justify-center">
                <Link
                  to="/skaermen-hjernen-og-trivselen"
                  className="inline-flex items-center justify-center gap-2 bg-white hover:bg-sage-100 text-pine-950 font-display font-bold text-sm uppercase px-6 py-4 rounded-xl shadow-lg transition-all"
                >
                  Se oplægget til skoler
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/skaermen-hjernen-og-trivselen#book-oplaeg"
                  className="inline-flex items-center justify-center gap-2 bg-pine-900/80 hover:bg-pine-900 text-white font-medium text-xs uppercase tracking-wider px-6 py-3.5 rounded-xl border border-sage-200/20 transition-all text-center"
                >
                  Book et uforpligtende møde
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Methodology Section */}
      <section className="py-24 bg-zinc-900 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-5xl font-bold mb-8 leading-tight">
                En struktur der virker: <br />
                <span className="text-emerald-400">Biologi → Mindset → Strategi</span>
              </h2>
              <p className="text-xl text-zinc-400 mb-10 leading-relaxed">
                Når vi arbejder sammen, starter vi ofte med biologien. Coaching har begrænset effekt, hvis dit nervesystem er overbelastet af stress, angst eller depression. Vi skaber roen først, så vi kan arbejde med dit mindset og dine strategier.
              </p>
              <div className="space-y-6">
                {[
                  { title: 'Biologi', desc: 'Forstå kroppens og nervesystemets reaktioner.', icon: <Zap className="h-6 w-6" /> },
                  { title: 'Mindset', desc: 'Arbejd med de ubevidste leveregler og mønstre.', icon: <Heart className="h-6 w-6" /> },
                  { title: 'Strategi', desc: 'Få værktøjerne til at mestre fremtidens udfordringer.', icon: <ShieldCheck className="h-6 w-6" /> }
                ].map((item, i) => (
                  <div key={i} className="flex items-start space-x-4">
                    <div className="bg-emerald-900/50 p-2 rounded-lg text-emerald-400">
                      {item.icon}
                    </div>
                    <div>
                      <h4 className="text-xl font-bold mb-1">{item.title}</h4>
                      <p className="text-zinc-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-emerald-500/10 blur-3xl rounded-full" />
              <img 
                src="/materiale-260428/Bio-mind-strat_gron2.svg" 
                alt="Trivlogi Model" 
                className="relative z-10 w-full max-w-md mx-auto drop-shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>


      {/* Testimonials */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-6">Det siger mine kunder</h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Hør fra dem, der allerede har genvundet overblikket og styrket deres trivsel.
          </p>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="grid grid-cols-1 md:grid-cols-3 gap-12"
              >
                {testimonials
                  .slice(currentIndex * itemsPerPage, (currentIndex + 1) * itemsPerPage)
                  .map((testimonial) => (
                    <div 
                      key={testimonial.id}
                      className="flex flex-col p-8 shadow-lg bg-white border border-zinc-50 h-full text-left"
                    >
                      <div className="mb-6">
                        <Quote className="h-8 w-8 text-emerald-900/10" />
                      </div>
                      <p className="text-lg text-zinc-800 font-medium leading-relaxed italic mb-8 flex-grow">
                        "{testimonial.text.length > 150 ? testimonial.text.substring(0, 150) + '...' : testimonial.text}"
                      </p>
                      <div className="pt-6 border-t border-zinc-100">
                        <h4 className="font-bold text-zinc-900 text-lg mb-0.5">{testimonial.name}</h4>
                        {testimonial.role ? (
                          <p className="text-emerald-700 text-xs uppercase tracking-wider font-semibold">{testimonial.role}</p>
                        ) : (
                          <p className="text-zinc-500 text-xs uppercase tracking-wider">{testimonial.age}</p>
                        )}
                      </div>
                    </div>
                  ))}
              </motion.div>
            </AnimatePresence>

            {/* Pagination Dots */}
            {totalPages > 1 && (
              <div className="flex justify-center space-x-3 mt-16">
                {[...Array(totalPages)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setCurrentIndex(i)}
                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                      currentIndex === i ? 'bg-emerald-900 w-8' : 'bg-zinc-200 hover:bg-zinc-400'
                    }`}
                    aria-label={`Gå til side ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="text-center mt-16">
            <Link to="/referencer" className="inline-flex items-center text-emerald-900 font-bold border-b-2 border-emerald-900 pb-1 hover:text-emerald-700 hover:border-emerald-700 transition-all">
              Se alle kundeudtalelser her <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-emerald-950 overflow-hidden relative">
        <div className="absolute inset-y-0 right-0 w-1/2 hidden lg:block">
          <img
            src="/materiale-260501/portræt/image00001.jpg"
            alt="Michael Lunde arbejder"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-emerald-950/20" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 items-stretch">
            <div className="py-20 lg:py-32 lg:pr-16 flex flex-col justify-center items-start text-left relative">
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-8 leading-tight">
                Klar til at tage <br /> det første skridt?
              </h2>
              <p className="text-zinc-300 text-xl mb-12 opacity-80 leading-relaxed max-w-lg">
                Du kan altid booke en gratis og åben samtale før du beslutter dig. Her fokuserer vi på at matche dine behov med mine erfaringer – helt uden tidsbegrænsning.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                <Link
                  to="/kontakt"
                  className="bg-white text-emerald-900 px-10 py-5 font-bold text-xl hover:-translate-y-0.5 hover:shadow-2xl transition-all shadow-xl text-center"
                >
                  Book gratis samtale
                </Link>
                <a
                  href={`mailto:${siteConfig.email}`}
                  className="bg-emerald-900 text-white border border-emerald-800 px-10 py-5 font-bold text-xl hover:bg-emerald-800 transition-all text-center flex items-center justify-center gap-3"
                >
                  Send en mail
                </a>
              </div>
              <p className="mt-8 text-emerald-100/60 font-medium">
                {siteConfig.email} • {siteConfig.phone}
              </p>
            </div>

            <div className="lg:hidden relative h-[300px] -mx-4 sm:-mx-6">
              <img
                src="/materiale-260501/portræt/image00001.jpg"
                alt="Michael Lunde"
                className="absolute inset-0 w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Modal for 1-1 kort.pdf */}
      <PdfModal
        isOpen={showKortPdfModal}
        onClose={() => setShowKortPdfModal(false)}
        pdfUrl="/_assets/1-1-kort.pdf"
        title="1-1 Kort overblik – Når det hele fylder for meget"
        description="TRIVlogi® · Biologien bag trivsel · Afklarende samtale"
      />
    </div>
  );
};

export default Home;
